import React from 'react';
import { Modal } from '../Modal';
import { Button } from '../Button/Button';
import './ConfirmDialog.scss';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  destructive?: boolean;
  isProcessing?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * Yes/no confirmation dialog. Thin preset over the generic Modal
 * shell — Modal owns the sizing, theming, backdrop/ESC/X-close
 * behavior, and the isProcessing gate. This component just wires
 * a short message + two footer buttons.
 *
 * Previously this had its own copy of the shell rules (background,
 * header padding, close button, etc.); those all lived in
 * ConfirmDialog.scss and had to stay in sync with the app-wide
 * .modal-content base. Consolidated so future confirm-flow tweaks
 * only need to touch Modal.
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  destructive = false,
  isProcessing = false,
  onConfirm,
  onCancel,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      title={title}
      onClose={onCancel}
      size="sm"
      isProcessing={isProcessing}
      className="confirm-dialog"
      footer={
        <>
          <Button
            variant="secondary"
            onClick={onCancel}
            disabled={isProcessing}
          >
            {cancelText}
          </Button>
          <Button
            variant={destructive ? 'danger' : 'primary'}
            onClick={onConfirm}
            disabled={isProcessing}
            autoFocus
          >
            {isProcessing ? 'Processing…' : confirmText}
          </Button>
        </>
      }
    >
      <p className="confirm-message">{message}</p>
    </Modal>
  );
};

export default ConfirmDialog;
